import {
  examplePacks,
  installPack,
  markPacksOffered,
  packsOffered,
  type RemovalPlan,
  type TrackSummary,
} from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '../../components/Button.js'
import { EmptyState } from '../../components/EmptyState.js'
import { useToast } from '../../components/Toast.js'
import { useServices } from '../../services/ServicesProvider.js'
import { RemoveTrackDialog } from './RemoveTrackDialog.js'
import { TrackRow } from './TrackRow.js'
import styles from './TrackList.module.css'

/**
 * What the user measures (T-301).
 *
 * The removal path is the reason this screen exists in its own ticket: a track
 * with history archives rather than disappearing (ADR-0003), and the UI has to
 * make that intelligible rather than appearing to ignore the request.
 */
export function TrackListScreen() {
  const { storage, repos } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()

  const [summaries, setSummaries] = useState<TrackSummary[] | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [offered, setOffered] = useState(true)
  const [plan, setPlan] = useState<RemovalPlan | null>(null)
  const [dragFrom, setDragFrom] = useState<number | null>(null)
  const [dragOver, setDragOver] = useState<number | null>(null)

  const reload = useCallback(async () => {
    const [all, alreadyOffered] = await Promise.all([
      repos.tracks.summaries({ includeArchived: true }),
      packsOffered(storage),
    ])
    setSummaries(all)
    setOffered(alreadyOffered)
  }, [repos, storage])

  useEffect(() => {
    void reload()
  }, [reload])

  const active = (summaries ?? []).filter((s) => !s.track.archived)
  const archived = (summaries ?? []).filter((s) => s.track.archived)

  const move = useCallback(
    async (from: number, to: number) => {
      if (to < 0 || to >= active.length || from === to) {
        return
      }
      const order = active.map((s) => s.track.id)
      const [moved] = order.splice(from, 1)
      if (moved === undefined) {
        return
      }
      order.splice(to, 0, moved)
      // Archived tracks keep their positions and are appended, so reordering
      // the visible list cannot silently renumber things it is not showing.
      await repos.tracks.reorder([...order, ...archived.map((s) => s.track.id)])
      await reload()
    },
    [active, archived, repos, reload],
  )

  async function confirmRemoval() {
    if (!plan) {
      return
    }
    const { track } = plan
    setPlan(null)
    const result = await repos.tracks.remove(track.id)
    await reload()

    if (!result.ok) {
      show({ message: result.errors[0]?.message ?? 'That did not work.', tone: 'danger' })
      return
    }
    show({
      message:
        result.value.kind === 'delete'
          ? `Deleted ${track.name}.`
          : `Archived ${track.name}. Its history is kept.`,
    })
  }

  async function installExamplePack(packId: string) {
    const pack = examplePacks.find((candidate) => candidate.id === packId)
    if (!pack) {
      return
    }
    const result = await installPack(pack, repos)
    if (!result.ok) {
      show({
        message: result.errors[0]?.message ?? 'Could not add those.',
        tone: 'danger',
      })
      return
    }
    await markPacksOffered(storage)
    await reload()
    show({ message: `Added ${String(result.value.tracks.length)} tracks.` })
  }

  async function declinePacks() {
    // Declining must leave the app completely empty and must not be asked
    // again (T-108).
    await markPacksOffered(storage)
    setOffered(true)
  }

  if (summaries === null) {
    return (
      <>
        <h1>Tracks</h1>
        <p>Loading…</p>
      </>
    )
  }

  return (
    <>
      <div className={styles['header']}>
        <h1>Tracks</h1>
        <Button variant="primary" onClick={() => void navigate('/tracks/new')}>
          New track
        </Button>
      </div>

      {active.length === 0 ? (
        <EmptyState
          title="Nothing to measure yet"
          body={
            offered
              ? 'A track is anything you want to record over time — a 1–10 scale, a yes/no, a set of options. Create one to get started.'
              : 'A track is anything you want to record over time. Start from an example below, or create your own — the examples are ordinary tracks you can rename, change or delete.'
          }
          action={
            offered ? undefined : (
              <div className={styles['packs']}>
                {examplePacks.map((pack) => (
                  <div key={pack.id} className={styles['pack']}>
                    <span>
                      <span className={styles['packName']}>{pack.name}</span>
                      <span className={styles['packDescription']}>
                        {' — '}
                        {pack.description}
                      </span>
                    </span>
                    <Button onClick={() => void installExamplePack(pack.id)}>Add</Button>
                  </div>
                ))}
                <Button variant="ghost" onClick={() => void declinePacks()}>
                  No thanks, I will make my own
                </Button>
              </div>
            )
          }
        />
      ) : (
        <div className={styles['list']}>
          {active.map((summary, index) => (
            <TrackRow
              key={summary.track.id}
              summary={summary}
              position={index}
              total={active.length}
              onMove={(from, to) => void move(from, to)}
              onArchive={() => {
                void repos.tracks.archive(summary.track.id).then(reload)
              }}
              onRestore={() => {
                void repos.tracks.restore(summary.track.id).then(reload)
              }}
              onRemove={() => {
                void repos.tracks.planRemoval(summary.track.id).then((result) => {
                  if (result.ok) {
                    setPlan(result.value)
                  }
                })
              }}
              dragging={dragFrom === index}
              dropTarget={dragOver === index && dragFrom !== index}
              onDragStart={() => setDragFrom(index)}
              onDragEnter={() => setDragOver(index)}
              onDragEnd={() => {
                if (dragFrom !== null && dragOver !== null) {
                  void move(dragFrom, dragOver)
                }
                setDragFrom(null)
                setDragOver(null)
              }}
            />
          ))}
        </div>
      )}

      {archived.length > 0 && (
        <section className={styles['archived']}>
          <button
            type="button"
            className={styles['disclosure']}
            aria-expanded={showArchived}
            onClick={() => setShowArchived((open) => !open)}
          >
            <svg
              className={`${styles['chevron']} ${showArchived ? styles['chevronOpen'] : ''}`}
              viewBox="0 0 20 20"
              width="14"
              height="14"
              aria-hidden="true"
            >
              <path
                d="M7 4l6 6-6 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Archived ({archived.length})
          </button>

          {showArchived && (
            <div className={styles['list']}>
              {archived.map((summary) => (
                <TrackRow
                  key={summary.track.id}
                  summary={summary}
                  position={0}
                  total={1}
                  onMove={() => undefined}
                  onArchive={() => undefined}
                  onRestore={() => {
                    void repos.tracks.restore(summary.track.id).then(reload)
                  }}
                  onRemove={() => {
                    void repos.tracks.planRemoval(summary.track.id).then((result) => {
                      if (result.ok) {
                        setPlan(result.value)
                      }
                    })
                  }}
                  dragging={false}
                  dropTarget={false}
                  onDragStart={() => undefined}
                  onDragEnter={() => undefined}
                  onDragEnd={() => undefined}
                />
              ))}
            </div>
          )}
        </section>
      )}

      <RemoveTrackDialog
        plan={plan}
        onCancel={() => setPlan(null)}
        onConfirm={() => void confirmRemoval()}
      />
    </>
  )
}
